/**
 * NonWorkingDaysTab — Ustawienia → Dni wolne od pracy.
 *
 * Kalendarz dni wolnych tenanta na API `/api/settings/non-working-days` (klient i hooki:
 * `lib/non-working-days/api.ts`). Bramka: `crm_core.non_working_days.manage` — zakładki nie ma
 * w Ustawieniach bez tego uprawnienia (`useSettingsSectionAccess`), a sam ekran dodatkowo
 * pokazuje komunikat „brak uprawnień" (jak `RecordProceduresTab`).
 *
 * Podział plików (`settings/non-working-days/`): czysta logika (filtr, duplikaty, walidacja,
 * rzutowanie na kalendarz) w `non-working-day-utils.ts`, komórka daty wiersza w
 * `DayDateCell.tsx`, okno kalendarza w `NonWorkingDayFormModal.tsx`; ten plik składa je z danymi i
 * mutacjami.
 *
 * Zasady ekranu:
 *  - wpis „Co roku" powtarza się w każdym roku (liczy się dzień i miesiąc), wpis jednorazowy
 *    dotyczy jednej konkretnej daty;
 *  - kolejność wierszy nadaje serwer: najpierw „Co roku" wg (miesiąc, dzień), potem jednorazowe
 *    wg daty — ekran niczego nie sortuje po swojej stronie;
 *  - duplikat = drugi wpis „Co roku" na ten sam dzień i miesiąc albo drugi jednorazowy na tę samą
 *    datę (jednorazowy na dzień objęty wpisem „Co roku" NIE jest blokowany — jak w serwerowej
 *    unikalności). Lokalna walidacja (pusta nazwa, duplikat) to szybka ścieżka przed wywołaniem
 *    API; serwer pozostaje rozstrzygającym (409 → ten sam komunikat inline);
 *  - każda zmiana idzie do API, a po niej lista jest pobierana ponownie z serwera — ekran pokazuje
 *    tylko to, co serwer potwierdził. Błąd inny niż duplikat → toast, a edytowany wiersz wraca do
 *    wartości z serwera (okno i wiersz roboczy zostają otwarte, żeby wpisane dane nie przepadły);
 *  - zapis „w toku" blokuje drugie żądanie tej samej akcji (przełącznik „Co roku" per wiersz,
 *    zapis wiersza roboczego, potwierdzenie usunięcia) — szybki drugi klik / Enter nie wysyła
 *    duplikatu;
 *  - dwa widoki tych samych danych (wspólny stan): „Lista" i „Kalendarz" (przełącznik
 *    `ListViewModeToggle`, wybór zapamiętany w localStorage przeglądarki);
 *  - lista: edycja w wierszu (bez okien) — data (`DayDateCell` z `DatePicker`), „Co roku"
 *    (`Checkbox`), nazwa (`InlineEditableCell`); nowy wpis to wiersz na górze listy w trybie
 *    edycji (Zapisz / Anuluj);
 *  - kalendarz: wspólna siatka `MonthGrid` z rzutowaniem wpisów „Co roku" na wyświetlany rok;
 *    klik w wpis albo w dzień otwiera okno (`Modal`) edycji / dodawania — okno zamiast edycji w
 *    wierszu, bo komórka siatki nie mieści pól, a callbacki `MonthGrid` nie zwracają elementu-kotwicy
 *    dla popovera (przekazują tylko id wpisu albo datę dnia);
 *  - usunięcie przez `ConfirmDialog` (miękkie, po stronie serwera), komunikaty przez `useToast()`.
 */

import {
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NON_WORKING_DAY_NAME_MAX_LENGTH, type ViewMode } from '@saas/shared';
import { Check, Plus, Trash2, X } from 'lucide-react';

import { InlineEditableCell } from '@/components/additional/InlineEditableCell';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { toLocalDateStr } from '@/components/calendar/calendar-utils';
import { MonthGrid } from '@/components/calendar/MonthGrid';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import {
  listViewModeOptions,
  ListViewModeToggle,
} from '@/components/configurable-table/ListViewModeToggle';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DatePicker } from '@/components/DatePicker';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import { Tabs } from '@/components/Tabs';
import { useToast } from '@/contexts/ToastContext';
import { useCanManageNonWorkingDays } from '@/hooks/useCanManageNonWorkingDays';
import {
  isNonWorkingDayDuplicateError,
  type NonWorkingDayDto,
  useCreateNonWorkingDay,
  useDeleteNonWorkingDay,
  useNonWorkingDaysList,
  useUpdateNonWorkingDay,
} from '@/lib/non-working-days/api';
import { cn } from '@/lib/utils';

import { DayDateCell } from './non-working-days/DayDateCell';
import {
  buildCalendarEvents,
  changedFields,
  type DayFilter,
  type DraftEntry,
  type DraftErrors,
  formatDayLabel,
  hasErrors,
  isDayFilter,
  isDuplicate,
  matchesFilter,
  NO_DRAFT_ERRORS,
  ONE_OFF_EVENT_COLOR,
  parseIsoYearMonth,
  validateEntryValues,
  YEARLY_EVENT_COLOR,
} from './non-working-days/non-working-day-utils';
import {
  NonWorkingDayFormModal,
  type NonWorkingDayModalTarget,
} from './non-working-days/NonWorkingDayFormModal';

/** Stabilna (ta sama) pusta lista na czas, gdy serwer jeszcze nic nie zwrócił. */
const NO_ENTRIES: readonly NonWorkingDayDto[] = [];

/** Jak długo błąd kolizji przy zaznaczaniu „Co roku" wisi pod datą, zanim zniknie sam. */
const DUPLICATE_ERROR_VISIBLE_MS = 5000;

/** Jak długo świeżo zapisany wiersz jest podświetlony. */
const ROW_FLASH_MS = 2200;

// ─── Tryb widoku (Lista / Kalendarz) ────────────────────────────────────────

type NonWorkingDaysViewMode = 'list' | 'calendar';

/** Wybór widoku pamiętany per przeglądarka (nie per użytkownik) — ekran działa też bez localStorage. */
const VIEW_MODE_STORAGE_KEY = 'settings.nonWorkingDays.viewMode';

const VIEW_MODE_OPTIONS = listViewModeOptions(['list', 'calendar']);

function readStoredViewMode(): NonWorkingDaysViewMode {
  try {
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'calendar' ? 'calendar' : 'list';
  } catch {
    // localStorage niedostępny (tryb prywatny, zablokowane dane witryn) — domyślnie lista.
    return 'list';
  }
}

function storeViewMode(mode: NonWorkingDaysViewMode): void {
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    /* localStorage niedostępny — wybór żyje tylko w pamięci do końca sesji. */
  }
}

// ─── Typy stanu ekranu ──────────────────────────────────────────────────────

interface ToggleError {
  id: string;
  message: string;
}

/**
 * Stan okna dodawania/edycji (kalendarz). Cel (`target`: dodawanie z datą albo edycja wpisu o id)
 * jest ustalany raz, przy otwarciu — nie wynika z tego, czy wpis wciąż jest na liście (patrz
 * nagłówek `NonWorkingDayFormModal`); `key` odświeża stan formularza przy każdym otwarciu.
 */
interface DialogState {
  key: number;
  target: NonWorkingDayModalTarget;
}

/** Pole nazwy w edycji wiersza — patrz `RowNameInput`. */
interface RowNameInputProps {
  entryId: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onBlur: () => void;
  /** Ref komórki `InlineEditableCell` — szerszy typ (input LUB textarea) niż element `Input`. */
  inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  disabled: boolean;
}

/**
 * Pole nazwy wiersza w trybie edycji. Osobny komponent, żeby callback-ref miał stałą tożsamość
 * (`useCallback`): inline-callback tworzony w `visible.map` byłby przy każdym renderze rodzica
 * odpinany (`null`) i podpinany od nowa.
 */
function RowNameInput({
  entryId,
  value,
  onChange,
  onKeyDown,
  onBlur,
  inputRef,
  disabled,
}: RowNameInputProps): ReactElement {
  const { t } = useTranslation();
  // Callback-ref przepisuje element do `ref` komórki bez rzutowania typów.
  const setInputRef = useCallback(
    (el: HTMLInputElement | null): void => {
      inputRef.current = el;
    },
    [inputRef],
  );
  return (
    <Input
      ref={setInputRef}
      size="sm"
      maxLength={NON_WORKING_DAY_NAME_MAX_LENGTH}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      disabled={disabled}
      aria-label={t('tenant.nonWorkingDays.col.name')}
      data-testid={`non-working-day-name-input-${entryId}`}
    />
  );
}

// ─── Główny komponent ───────────────────────────────────────────────────────

export function NonWorkingDaysTab(): ReactElement {
  const { t } = useTranslation();
  const canManage = useCanManageNonWorkingDays();

  // Bez uprawnienia nie montujemy treści w ogóle — inaczej poleciałoby zapytanie o listę (403).
  if (!canManage) {
    return (
      <Card padding="md" data-testid="non-working-days-no-permission">
        <p className="text-sm text-text-tertiary">{t('tenant.nonWorkingDays.noPermission')}</p>
      </Card>
    );
  }
  return <NonWorkingDaysContent />;
}

function NonWorkingDaysContent(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const draftDayErrorId = useId();

  const listQ = useNonWorkingDaysList();
  const createMutation = useCreateNonWorkingDay();
  const updateMutation = useUpdateNonWorkingDay();
  const deleteMutation = useDeleteNonWorkingDay();

  // Kolejność z serwera (patrz nagłówek pliku) — bez własnego sortowania.
  const entries = listQ.data ?? NO_ENTRIES;
  const hasData = listQ.data !== undefined;

  const [filter, setFilter] = useState<DayFilter>('all');
  const [draft, setDraft] = useState<DraftEntry | null>(null);
  const [draftErrors, setDraftErrors] = useState<DraftErrors>(NO_DRAFT_ERRORS);
  const [pendingDelete, setPendingDelete] = useState<NonWorkingDayDto | null>(null);
  const [toggleError, setToggleError] = useState<ToggleError | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashRowRef = useRef<HTMLTableRowElement | null>(null);

  // Blokady „w toku" (ref, nie stan — drugi szybki klik / Enter w tym samym takcie widzi je od
  // razu, zanim React zdąży przerysować): zapis wiersza roboczego, przełączanie „Co roku" per wiersz
  // i potwierdzenie usunięcia.
  const draftSavingRef = useRef(false);
  const deletingRef = useRef(false);
  const togglingIdsRef = useRef<Set<string>>(new Set());

  // Widok: lista albo kalendarz (te same dane). Kalendarz ma własny rok/miesiąc, startuje od dziś.
  const [viewMode, setViewMode] = useState<NonWorkingDaysViewMode>(readStoredViewMode);
  const [calendarCursor, setCalendarCursor] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const dialogSeq = useRef(0);

  // Callback-ref o stałej tożsamości: fokus na nazwie nowego wpisu tylko przy jego zamontowaniu
  // (inline-ref fokusowałby pole przy każdym renderze i odbierał fokus datownikowi).
  const focusOnMount = useCallback((el: HTMLInputElement | null): void => {
    el?.focus();
  }, []);

  const visible = useMemo(
    () => entries.filter((e) => matchesFilter(e.repeatsYearly, filter)),
    [entries, filter],
  );
  const yearlyCount = useMemo(() => entries.filter((e) => e.repeatsYearly).length, [entries]);
  const calendarEvents = useMemo(
    () => buildCalendarEvents(entries, filter, calendarCursor.year),
    [entries, filter, calendarCursor.year],
  );
  const calendarLegend = useMemo(
    () => [
      { label: t('tenant.nonWorkingDays.legend.yearly'), color: YEARLY_EVENT_COLOR },
      { label: t('tenant.nonWorkingDays.legend.oneOff'), color: ONE_OFF_EVENT_COLOR },
    ],
    [t],
  );
  // Edytowany wpis oglądany „na żywo" z listy; `null` w trybie edycji = wpis zniknął po otwarciu okna,
  // a w trybie dodawania (i bez okna) zawsze `null`.
  const dialogTarget = dialog?.target ?? null;
  const editedEntryId = dialogTarget?.mode === 'edit' ? dialogTarget.entryId : null;
  const dialogEntry =
    editedEntryId === null ? null : (entries.find((e) => e.id === editedEntryId) ?? null);

  // Błąd kolizji przy zaznaczaniu „Co roku" znika sam — nie wisi pod datą w nieskończoność.
  useEffect(() => {
    if (toggleError === null) return;
    const timer = window.setTimeout(() => setToggleError(null), DUPLICATE_ERROR_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [toggleError]);

  // Po zapisie wiersz może wylądować gdzie indziej (kolejność serwera) — podświetlamy go na
  // chwilę i doprowadzamy do widoku.
  useEffect(() => {
    if (flashId === null) return;
    flashRowRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    const timer = window.setTimeout(() => setFlashId(null), ROW_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashId]);

  const notifySaved = (id: string): void => {
    setFlashId(id);
    toast.show({ variant: 'success', title: t('tenant.nonWorkingDays.toast.saved') });
  };

  const notifyError = (messageKey: string): void => {
    toast.show({ variant: 'error', title: t(messageKey) });
  };

  /** Zapisany wpis, który nie pasuje do aktywnego filtru, zniknąłby z widoku — przełączamy na „Wszystkie". */
  const revealInFilter = (repeatsYearly: boolean): void => {
    setFilter((current) => (matchesFilter(repeatsYearly, current) ? current : 'all'));
  };

  // ── edycja istniejących wierszy ──

  /** Zwraca komunikat błędu (komórka zostaje w edycji) albo `null` (zapisano / błąd zgłoszony toastem). */
  const commitDay = async (entry: NonWorkingDayDto, day: string): Promise<string | null> => {
    if (isDuplicate(entries, { day, repeatsYearly: entry.repeatsYearly }, entry.id)) {
      return t('tenant.nonWorkingDays.duplicate');
    }
    setToggleError(null);
    try {
      await updateMutation.mutateAsync({ id: entry.id, values: { day } });
    } catch (err) {
      if (isNonWorkingDayDuplicateError(err)) return t('tenant.nonWorkingDays.duplicate');
      notifyError('tenant.nonWorkingDays.toast.saveError');
      return null;
    }
    notifySaved(entry.id);
    return null;
  };

  const toggleYearly = async (entry: NonWorkingDayDto, repeatsYearly: boolean): Promise<void> => {
    // Poprzednie przełączenie tego wiersza jeszcze trwa — drugi klik nie wysyła drugiego żądania.
    if (togglingIdsRef.current.has(entry.id)) return;
    const duplicateMessage = t('tenant.nonWorkingDays.duplicate');
    if (isDuplicate(entries, { day: entry.day, repeatsYearly }, entry.id)) {
      setToggleError({ id: entry.id, message: duplicateMessage });
      return;
    }
    setToggleError(null);
    togglingIdsRef.current.add(entry.id);
    try {
      await updateMutation.mutateAsync({ id: entry.id, values: { repeatsYearly } });
    } catch (err) {
      // Pole „Co roku" jest sterowane danymi z serwera — przy błędzie samo wraca do starej wartości.
      if (isNonWorkingDayDuplicateError(err)) {
        setToggleError({ id: entry.id, message: duplicateMessage });
      } else {
        notifyError('tenant.nonWorkingDays.toast.saveError');
      }
      return;
    } finally {
      togglingIdsRef.current.delete(entry.id);
    }
    revealInFilter(repeatsYearly);
    notifySaved(entry.id);
  };

  /**
   * Pusta nazwa → odrzucenie (komórka zostaje w edycji z błędem inline); inaczej zapis. Błąd
   * serwera NIE odrzuca obietnicy: komórka ma się zamknąć i pokazać nazwę z serwera, a powód
   * dostaje użytkownik w toaście.
   */
  const commitName = (entry: NonWorkingDayDto, next: string): Promise<void> | undefined => {
    const name = next.trim();
    if (name === '') {
      return Promise.reject(new Error(t('tenant.nonWorkingDays.nameRequired')));
    }
    if (name === entry.name) return undefined;
    setToggleError(null);
    return updateMutation.mutateAsync({ id: entry.id, values: { name } }).then(
      () => notifySaved(entry.id),
      () => notifyError('tenant.nonWorkingDays.toast.saveError'),
    );
  };

  const confirmDelete = async (): Promise<void> => {
    // Poprzednie usuwanie jeszcze trwa — drugi klik „Usuń" nie wysyła drugiego DELETE.
    if (pendingDelete === null || deletingRef.current) return;
    deletingRef.current = true;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
    } catch {
      setPendingDelete(null);
      notifyError('tenant.nonWorkingDays.toast.deleteError');
      return;
    } finally {
      deletingRef.current = false;
    }
    setPendingDelete(null);
    // Usunięcie z okna edycji (kalendarz) zamyka też samo okno; z listy — bez efektu.
    setDialog(null);
    toast.show({ variant: 'success', title: t('tenant.nonWorkingDays.toast.deleted') });
  };

  // ── widok kalendarza ──

  const changeViewMode = (mode: ViewMode): void => {
    if (mode !== 'list' && mode !== 'calendar') return;
    setViewMode(mode);
    storeViewMode(mode);
  };

  const goPrevMonth = (): void => {
    setCalendarCursor((c) =>
      c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 },
    );
  };

  const goNextMonth = (): void => {
    setCalendarCursor((c) =>
      c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 },
    );
  };

  const goToday = (): void => {
    const now = new Date();
    setCalendarCursor({ year: now.getFullYear(), month: now.getMonth() });
  };

  /** Otwiera okno: edycja wpisu o danym id albo dodawanie z podaną datą startową. */
  const openDialog = (target: NonWorkingDayModalTarget): void => {
    dialogSeq.current += 1;
    setDialog({ key: dialogSeq.current, target });
  };

  /** „Dodaj dzień wolny" w kalendarzu: dziś, a gdy oglądamy inny miesiąc — jego pierwszy dzień. */
  const defaultCalendarDay = (): string => {
    const now = new Date();
    if (calendarCursor.year === now.getFullYear() && calendarCursor.month === now.getMonth()) {
      return toLocalDateStr(now);
    }
    const year = String(calendarCursor.year).padStart(4, '0');
    const month = String(calendarCursor.month + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const handleAdd = (): void => {
    if (viewMode === 'list') {
      startDraft();
      return;
    }
    openDialog({ mode: 'create', initialDay: defaultCalendarDay() });
  };

  /**
   * Klik w pole dnia: gdy wypada na nim dzień wolny — edycja jego wpisu, inaczej dodawanie z tą
   * datą. Gdy na dniu jest kilka wpisów (np. „Co roku" i jednorazowy na tę samą datę), klik w samo
   * pole otwiera edycję PIERWSZEGO z nich — w kolejności z serwera, czyli „Co roku" przed
   * jednorazowymi. Pozostałe wpisy dnia otwiera się klikiem w ich własny pasek (każdy wpis ma swój
   * pasek z `onEventClick`).
   */
  const handleCalendarDayClick = (date: Date): void => {
    const dayKey = toLocalDateStr(date);
    const hit = calendarEvents.find((ev) => ev.startKey === dayKey);
    if (hit) openDialog({ mode: 'edit', entryId: hit.id });
    else openDialog({ mode: 'create', initialDay: dayKey });
  };

  /** Zwraca komunikat błędu daty (kolizja z serwera) albo `null` — patrz `onSave` okna. */
  const saveFromDialog = async (values: DraftEntry): Promise<string | null> => {
    if (dialogTarget === null) return null;
    if (dialogTarget.mode === 'edit' && dialogEntry === null) {
      // Edytowany wpis zniknął z listy (np. usunięty przez kogoś innego) — nie ma czego zapisać.
      notifyError('tenant.nonWorkingDays.toast.saveError');
      setDialog(null);
      return null;
    }

    let saved: NonWorkingDayDto;
    try {
      if (dialogEntry === null) {
        saved = await createMutation.mutateAsync(values);
      } else {
        const changes = changedFields(dialogEntry, values);
        if (Object.keys(changes).length === 0) {
          // Nic się nie zmieniło — nie ma po co pytać serwera.
          setDialog(null);
          return null;
        }
        saved = await updateMutation.mutateAsync({ id: dialogEntry.id, values: changes });
      }
    } catch (err) {
      if (isNonWorkingDayDuplicateError(err)) return t('tenant.nonWorkingDays.duplicate');
      notifyError('tenant.nonWorkingDays.toast.saveError');
      return null;
    }

    setToggleError(null);
    revealInFilter(saved.repeatsYearly);
    // Zapisany wpis ma być widoczny: kalendarz przeskakuje na jego miesiąc (i rok dla jednorazowego).
    const { year: savedYear, month: savedMonth } = parseIsoYearMonth(saved.day);
    setCalendarCursor((c) => ({
      year: saved.repeatsYearly ? c.year : savedYear,
      month: savedMonth,
    }));
    setDialog(null);
    notifySaved(saved.id);
    return null;
  };

  // ── nowy wpis ──

  const startDraft = (): void => {
    setDraft({ day: toLocalDateStr(new Date()), repeatsYearly: false, name: '' });
    setDraftErrors(NO_DRAFT_ERRORS);
  };

  const cancelDraft = (): void => {
    setDraft(null);
    setDraftErrors(NO_DRAFT_ERRORS);
  };

  /** Zmiana jednego pola wiersza roboczego czyści błąd tego pola. */
  const patchDraft = (patch: Partial<DraftEntry>, clear: keyof DraftErrors): void => {
    setDraft((prev) => (prev === null ? prev : { ...prev, ...patch }));
    setDraftErrors((prev) => (clear === 'day' ? { ...prev, day: null } : { ...prev, name: null }));
  };

  const saveDraft = async (): Promise<void> => {
    // Poprzedni zapis wiersza roboczego jeszcze trwa — drugi klik / Enter nie wysyła drugiego POST.
    if (draft === null || draftSavingRef.current) return;
    const errors = validateEntryValues(draft, entries, t);
    if (hasErrors(errors)) {
      setDraftErrors(errors);
      return;
    }
    draftSavingRef.current = true;
    let created: NonWorkingDayDto;
    try {
      created = await createMutation.mutateAsync({
        day: draft.day,
        repeatsYearly: draft.repeatsYearly,
        name: draft.name.trim(),
      });
    } catch (err) {
      // Wiersz roboczy zostaje — wpisane dane nie przepadają, można poprawić i zapisać ponownie.
      if (isNonWorkingDayDuplicateError(err)) {
        setDraftErrors((prev) => ({ ...prev, day: t('tenant.nonWorkingDays.duplicate') }));
      } else {
        notifyError('tenant.nonWorkingDays.toast.saveError');
      }
      return;
    } finally {
      draftSavingRef.current = false;
    }
    revealInFilter(created.repeatsYearly);
    cancelDraft();
    notifySaved(created.id);
  };

  const handleDraftNameKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveDraft();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelDraft();
    }
  };

  const showEmpty = visible.length === 0 && draft === null;

  return (
    // Kalendarz wypełnia wysokość obszaru zakładki (`settings-tab-content` daje definitywną
    // wysokość, wzorzec Słowników); lista zostaje w zwykłym przepływie i przewija się z kartą.
    <div
      className={hasData && viewMode === 'calendar' ? 'flex h-full min-h-0 flex-col' : undefined}
      data-testid="non-working-days-tab"
    >
      <p className="mb-4 max-w-2xl text-sm text-text-secondary">
        {t('tenant.nonWorkingDays.description')}
      </p>

      {listQ.isError && (
        <Alert
          tone="error"
          className="mb-4"
          actions={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void listQ.refetch()}
              data-testid="non-working-days-retry"
            >
              {t('tenant.nonWorkingDays.retry')}
            </Button>
          }
          data-testid="non-working-days-load-error"
        >
          {t('tenant.nonWorkingDays.loadError')}
        </Alert>
      )}

      {listQ.isPending && (
        <div
          className="flex items-center justify-center py-10"
          data-testid="non-working-days-loading"
        >
          <Spinner />
        </div>
      )}

      {hasData && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <Tabs
              idPrefix="non-working-days-filter"
              value={filter}
              onChange={(value) => {
                if (isDayFilter(value)) setFilter(value);
              }}
              items={[
                {
                  value: 'all',
                  label: t('tenant.nonWorkingDays.filter.all', { n: entries.length }),
                  content: null,
                  testId: 'non-working-days-filter-all',
                },
                {
                  value: 'yearly',
                  label: t('tenant.nonWorkingDays.filter.yearly', { n: yearlyCount }),
                  content: null,
                  testId: 'non-working-days-filter-yearly',
                },
                {
                  value: 'oneOff',
                  label: t('tenant.nonWorkingDays.filter.oneOff', {
                    n: entries.length - yearlyCount,
                  }),
                  content: null,
                  testId: 'non-working-days-filter-one-off',
                },
              ]}
            />
            <div className="flex items-center gap-2">
              <ListViewModeToggle
                value={viewMode}
                onChange={changeViewMode}
                modes={VIEW_MODE_OPTIONS}
                testIdPrefix="non-working-days"
              />
              {/* Lista: nowy wpis = wiersz roboczy na górze (jeden naraz). Kalendarz: okno dodawania. */}
              <Button
                variant="primary"
                size="lg"
                leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
                onClick={handleAdd}
                disabled={viewMode === 'list' && draft !== null}
                data-testid="non-working-days-add-btn"
              >
                {t('tenant.nonWorkingDays.add')}
              </Button>
            </div>
          </div>

          {viewMode === 'list' ? (
            <Card padding="none">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="non-working-days-table">
                  <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                    <tr>
                      <th className="w-56 px-4 py-2 text-left">
                        {t('tenant.nonWorkingDays.col.date')}
                      </th>
                      <th className="w-28 px-4 py-2 text-center">
                        {t('tenant.nonWorkingDays.col.yearly')}
                      </th>
                      <th className="px-4 py-2 text-left">{t('tenant.nonWorkingDays.col.name')}</th>
                      <th className="w-24 px-4 py-2">
                        <span className="sr-only">{t('tenant.nonWorkingDays.col.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-primary">
                    {draft !== null && (
                      <tr
                        className="bg-accent-500/5 align-top"
                        data-testid="non-working-day-draft-row"
                      >
                        <td className="px-4 py-2">
                          <DatePicker
                            value={draft.day}
                            onChange={(value) => patchDraft({ day: value }, 'day')}
                            invalid={draftErrors.day !== null}
                            className="w-44"
                            aria-label={t('tenant.nonWorkingDays.col.date')}
                            aria-describedby={
                              draftErrors.day !== null ? draftDayErrorId : undefined
                            }
                            data-testid="non-working-day-draft-date"
                          />
                          {draftErrors.day !== null && (
                            <p
                              id={draftDayErrorId}
                              role="alert"
                              className="mt-0.5 text-xs text-red-600 dark:text-red-400"
                              data-testid="non-working-day-draft-date-error"
                            >
                              {draftErrors.day}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex h-8 justify-center">
                            <Checkbox
                              checked={draft.repeatsYearly}
                              onChange={(e) =>
                                patchDraft({ repeatsYearly: e.target.checked }, 'day')
                              }
                              aria-label={t('tenant.nonWorkingDays.col.yearly')}
                              data-testid="non-working-day-draft-yearly"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <Input
                            ref={focusOnMount}
                            size="sm"
                            maxLength={NON_WORKING_DAY_NAME_MAX_LENGTH}
                            value={draft.name}
                            onChange={(e) => patchDraft({ name: e.target.value }, 'name')}
                            onKeyDown={handleDraftNameKeyDown}
                            placeholder={t('tenant.nonWorkingDays.namePlaceholder')}
                            aria-label={t('tenant.nonWorkingDays.col.name')}
                            error={draftErrors.name ?? undefined}
                            data-testid="non-working-day-draft-name"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <IconButton
                              size="sm"
                              aria-label={t('tenant.nonWorkingDays.action.saveDraft')}
                              onClick={() => void saveDraft()}
                              disabled={createMutation.isPending}
                              data-testid="non-working-day-draft-save"
                            >
                              <Check aria-hidden="true" />
                            </IconButton>
                            <IconButton
                              size="sm"
                              aria-label={t('tenant.nonWorkingDays.action.cancelDraft')}
                              onClick={cancelDraft}
                              data-testid="non-working-day-draft-cancel"
                            >
                              <X aria-hidden="true" />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    )}

                    {visible.map((entry) => {
                      const isFlashing = entry.id === flashId;
                      return (
                        <tr
                          key={entry.id}
                          ref={isFlashing ? flashRowRef : undefined}
                          className={cn(
                            'align-top transition-colors',
                            isFlashing && 'bg-accent-500/10',
                          )}
                          data-testid={`non-working-day-row-${entry.id}`}
                        >
                          <td className="px-4 py-2">
                            <DayDateCell
                              entry={entry}
                              onCommit={(day) => commitDay(entry, day)}
                              externalError={
                                toggleError?.id === entry.id ? toggleError.message : null
                              }
                            />
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex h-7 items-center justify-center">
                              <Checkbox
                                checked={entry.repeatsYearly}
                                onChange={(e) => void toggleYearly(entry, e.target.checked)}
                                aria-label={t('tenant.nonWorkingDays.row.yearlyAria', {
                                  name: entry.name,
                                })}
                                data-testid={`non-working-day-yearly-${entry.id}`}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <InlineEditableCell<string>
                              value={entry.name}
                              onCommit={(next) => commitName(entry, next)}
                              renderInput={(
                                value,
                                onChange,
                                onKeyDown,
                                onBlur,
                                ref,
                                isCommitting,
                              ) => (
                                <RowNameInput
                                  entryId={entry.id}
                                  value={value}
                                  onChange={onChange}
                                  onKeyDown={onKeyDown}
                                  onBlur={onBlur}
                                  inputRef={ref}
                                  disabled={isCommitting}
                                />
                              )}
                              renderDisplay={(value) => (
                                <span className="text-text-primary">{value}</span>
                              )}
                              className="-mx-2 min-h-7 rounded px-2 py-1 hover:bg-bg-tertiary"
                              data-testid={`non-working-day-name-${entry.id}`}
                            />
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end">
                              <IconButton
                                size="sm"
                                aria-label={t('tenant.nonWorkingDays.action.delete', {
                                  name: entry.name,
                                })}
                                onClick={() => setPendingDelete(entry)}
                                data-testid={`non-working-day-delete-${entry.id}`}
                              >
                                <Trash2 aria-hidden="true" />
                              </IconButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {showEmpty && (
                      <tr data-testid="non-working-days-empty">
                        <td colSpan={4} className="px-4 py-10 text-center text-sm text-text-muted">
                          {entries.length === 0
                            ? t('tenant.nonWorkingDays.empty.none')
                            : t('tenant.nonWorkingDays.empty.filtered')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <div className="min-h-0 flex-1">
              <MonthGrid
                year={calendarCursor.year}
                month={calendarCursor.month}
                events={calendarEvents}
                legend={calendarLegend}
                onPrevMonth={goPrevMonth}
                onNextMonth={goNextMonth}
                onToday={goToday}
                onEventClick={(eventId) => openDialog({ mode: 'edit', entryId: eventId })}
                onDayClick={handleCalendarDayClick}
                fillHeight
                testId="non-working-days-calendar"
              />
            </div>
          )}
        </>
      )}

      {dialog !== null && (
        <NonWorkingDayFormModal
          key={dialog.key}
          target={dialog.target}
          entry={dialogEntry}
          entries={entries}
          onSave={saveFromDialog}
          onRequestDelete={() => {
            if (dialogEntry !== null) setPendingDelete(dialogEntry);
          }}
          onClose={() => setDialog(null)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.nonWorkingDays.confirmDelete.title')}
        message={
          pendingDelete === null
            ? ''
            : t(
                pendingDelete.repeatsYearly
                  ? 'tenant.nonWorkingDays.confirmDelete.messageYearly'
                  : 'tenant.nonWorkingDays.confirmDelete.message',
                {
                  name: pendingDelete.name,
                  date: formatDayLabel(pendingDelete.day, pendingDelete.repeatsYearly),
                },
              )
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        confirming={deleteMutation.isPending}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
